import React,{useState} from "react";
import { Card, Button, Input,Select,TimePicker, Tooltip, Switch,Modal } from "antd";
import { 
    DeleteOutlined, FormOutlined, MessageOutlined, 
    ClockCircleOutlined, PlayCircleOutlined, StopOutlined,
    CheckCircleOutlined, TeamOutlined,
    DownOutlined, UpOutlined,
    PlusOutlined, MinusOutlined
} from "@ant-design/icons";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { getColorFromStatus, getTextColorForStatus, mapIndexToStatus,mapStatusToIndex,days } from "../../core/utils/weekPlanUtils";
import { dayStatuses, getDateFromWeekAndDay } from "../../core/utils/weekPlanUtils";
import styles from "./WeekPlan.module.css";
import { WorkHoursShouldCalcuated } from "../../core/weekplan/weekplanRules";
import logger from "../../core/utils/logger";
import { useTranslation } from 'react-i18next';
const { Option } = Select;

dayjs.extend(utc);
dayjs.extend(customParseFormat);

// Helper function to create a default work duration
const createDefaultWorkDuration = (startTime = "08:00", endTime = "12:00") => {
  const defaultDate = dayjs().format("YYYY-MM-DD");
  // Parse as local time and convert to UTC properly
  const startLocal = dayjs(`${defaultDate} ${startTime}`, "YYYY-MM-DD HH:mm");
  const endLocal = dayjs(`${defaultDate} ${endTime}`, "YYYY-MM-DD HH:mm");
  return {
    id: 0, // Temporary ID for new durations
    startTime: dayjs.utc(startLocal.valueOf()).toDate(),
    endTime: dayjs.utc(endLocal.valueOf()).toDate(),
  };
};

// Helper function to suggest next time slot based on existing durations
const suggestNextTimeSlot = (existingDurations) => {
  if (!existingDurations || existingDurations.length === 0) {
    return { startTime: "08:00", endTime: "12:00" };
  }
  
  // Sort durations by start time and find the latest end time
  const sortedDurations = existingDurations
    .filter(d => d.startTime && d.endTime)
    .sort((a, b) => dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf());
  
  if (sortedDurations.length === 0) {
    return { startTime: "08:00", endTime: "12:00" };
  }
  
  const lastDuration = sortedDurations[sortedDurations.length - 1];
  const lastEndTime = dayjs(lastDuration.endTime);
  
  // Suggest starting 1 hour after the last end time
  const suggestedStart = lastEndTime.add(1, 'hour');
  const suggestedEnd = suggestedStart.add(4, 'hour'); // Default 4-hour duration
  
  return {
    startTime: suggestedStart.format('HH:mm'),
    endTime: suggestedEnd.format('HH:mm')
  };
};

// Validation function for work duration times
const validateWorkDuration = (duration, allDurations, currentIndex) => {
  const errors = [];
  
  if (!duration.startTime || !duration.endTime) {
    errors.push("Both start and end times are required");
    return errors;
  }
  
  const startTime = dayjs(duration.startTime);
  const endTime = dayjs(duration.endTime);
  
  // Check if start time is before end time
  if (startTime.isAfter(endTime) || startTime.isSame(endTime)) {
    errors.push("Start time must be before end time");
  }
  
  // Check for overlaps with other durations
  for (let i = 0; i < allDurations.length; i++) {
    if (i === currentIndex) continue; // Skip self
    
    const otherDuration = allDurations[i];
    if (!otherDuration.startTime || !otherDuration.endTime) continue;
    
    const otherStart = dayjs(otherDuration.startTime);
    const otherEnd = dayjs(otherDuration.endTime);
    
    // Check for overlap
    if (
      (startTime.isBefore(otherEnd) && endTime.isAfter(otherStart)) ||
      (startTime.isSame(otherStart)) || (endTime.isSame(otherEnd))
    ) {
      errors.push(`Time overlaps with another work period (${otherStart.format('HH:mm')} - ${otherEnd.format('HH:mm')})`);
      break;
    }
  }
  
  // Check reasonable working hours (between 5:00 and 23:00)
  if (startTime.hour() < 5 || endTime.hour() > 23) {
    errors.push("Working hours should be between 05:00 and 23:00");
  }
  
  // Check maximum duration (12 hours)
  const durationHours = endTime.diff(startTime, 'hour', true);
  if (durationHours > 12) {
    errors.push("Maximum work duration is 12 hours");
  }
  
  // Check minimum duration (15 minutes)
  if (durationHours < 0.25) {
    errors.push("Minimum work duration is 15 minutes");
  }
  
  return errors;
};

// Convert time value to dayjs local time (for display in TimePicker)
const toLocalTime = (timeValue) => {
    if (!timeValue) return null;
    
    const isValid = dayjs(timeValue).isValid();
    if (!isValid) return null;
    
    // Check if it's already a Date object (local time) or a UTC string
    if (timeValue instanceof Date) {
        // It's already a local Date object, just convert to dayjs
        return dayjs(timeValue);
    }
    
    // If it's a string, check if it has timezone information
    if (typeof timeValue === 'string') {
        const hasZ = timeValue.includes('Z');
        const hasOffset = timeValue.includes('+') || timeValue.match(/-\d{2}:/);
        
        if (hasZ || hasOffset) {
            // Has timezone info, treat as UTC and convert to local
            return dayjs.utc(timeValue).local();
        } else {
            // No timezone info, assume it's already local
            return dayjs(timeValue);
        }
    }
    
    // Fallback: treat as local time
    return dayjs(timeValue);
};

// Helper function: Translate titles from database values to localized display
const translateTitle = (title, t) => {
  if (!title) return '';
  
  const titleTranslations = {
    'Mr.': t('register.mr'),
    'Mrs.': t('register.mrs'), 
    'Ms.': t('register.ms'),
    'Mx.': t('register.mx'),
    // Academic/Medical titles are usually the same across languages
    'Dr.': 'Dr.',
    'Prof.': 'Prof.',
    'Prof. Dr.': 'Prof. Dr.',
    'Dr. med.': 'Dr. med.',
    'Prof. Dr. med.': 'Prof. Dr. med.'
  };
  
  return titleTranslations[title] || title;
};

// Helper function: Format user display name by looking up user data
const formatUserName = (currentWeekPlan, users, t) => {
   
  if (!users || !currentWeekPlan.userId) {
    return currentWeekPlan.userName || 'Unknown User';
  }
  
  // Look up the user by userId from the users array
  const user = users.find(u => u.id == currentWeekPlan.userId);
  
  if (user && (user.firstName || user.surname)) {
    // If we found the user and they have personal info, use it
    const parts = [
      translateTitle(user.title?.trim(), t),
      user.firstName?.trim(),
      user.surname?.trim()
    ].filter(Boolean);
    
    
    return parts.length > 0 ? parts.join(' ') : user.name || currentWeekPlan.userName || 'Unknown User';
  }
  
  // Fallback to userName field for existing users or if user not found
  return currentWeekPlan.userName || 'Unknown User';
};

const calculateTotalWorkDuration = (dailyPlans) => {
    let totalMinutes = 0;

    dailyPlans.forEach(plan => {
        const status = mapIndexToStatus(plan.dayStatus);
        if(!WorkHoursShouldCalcuated( status )){
            return;
        }
        plan.workDurationEmployees.forEach(duration => {
            
            const start = dayjs.utc(duration.startTime);
            const end = dayjs.utc(duration.endTime);

            if (start.isValid() && end.isValid() && end.isAfter(start)) {
                const diff = end.diff(start, 'minute');
                totalMinutes += diff;
            }
        });
    });

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return { hours, minutes };
};
const calculateWorkDurationDifference = (shouldMinutes, currentMinutes) => {
    const totalDiff = currentMinutes - shouldMinutes;

    const sign = totalDiff < 0 ? -1 : 1;
    const absMinutes = Math.abs(totalDiff);

    const diffHours = Math.floor(absMinutes / 60) * sign;
    const diffMinutesRemaining = (absMinutes % 60) * sign;

    return { diffHours, diffMinutesRemaining };
};
const formatDurationDifference = (shouldMinutes, currentMinutes) => {
    const totalDiff = currentMinutes - shouldMinutes;

    const sign = totalDiff < 0 ? "-" : "+";
    const absMinutes = Math.abs(totalDiff);

    const hours = Math.floor(absMinutes / 60);
    const minutes = absMinutes % 60;

    return `${sign} ${hours}h ${minutes}min`;
};





export const WeekPlanCard=({
    currentWeekPlan,
    currentCulture,
    updateCurrentWeekPlan,
    deleteUserWeekPlan, 
    isMobile, 
    handleTimeChange,
    updateDayStatus,
    selectedWeek,
    hasMediAdminRole, 
    handleActivateChanges,
    handleCommentChange,
    handleSubOrganisationChange,
    subOrganisations,
    selectedYear,
    weekPlanSettings,
    users,
    // New props for work duration management
    handleAddWorkDuration,
    handleRemoveWorkDuration,
    // Epic 33: hide the restore-from-template button when embedded (e.g. the
    // Summary "edit week" modal), where regenerating the week isn't wanted.
    hideRestore = false,
    // Epic 33: hide the activate switch in the summary modal — activating a
    // (usually past) week from the summary isn't a meaningful action there.
    hideActivate = false})=>{
    // Enhanced state management for better UX
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentDay, setCurrentDay] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [comment, setComment] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [hoveredDay, setHoveredDay] = useState(null);
    const [expandedCard, setExpandedCard] = useState(false);
    // Initialize all time sections as expanded
    const [collapsedTimeSections, setCollapsedTimeSections] = useState(() => {
        const initialState = {};
        days.forEach(day => {
            initialState[day] = false; // false = expanded
        });
        return initialState;
    });
    const [localTimeValues, setLocalTimeValues] = useState({});
    
    const calendar = currentWeekPlan.calendarEmployees;
    const employee = currentWeekPlan.userName;
    const { t } = useTranslation();
    
    // Fallback function for day name translation
    const getDayName = (day) => {
        const translatedDay = t('days.' + day.toLowerCase());
        // If translation fails (returns the key), use a fallback
        if (translatedDay === 'days.' + day.toLowerCase()) {
            // Fallback day names
            const dayNames = {
                monday: { en: 'Monday', de: 'Montag' },
                tuesday: { en: 'Tuesday', de: 'Dienstag' },
                wednesday: { en: 'Wednesday', de: 'Mittwoch' },
                thursday: { en: 'Thursday', de: 'Donnerstag' },
                friday: { en: 'Friday', de: 'Freitag' },
                saturday: { en: 'Saturday', de: 'Samstag' },
                sunday: { en: 'Sunday', de: 'Sonntag' }
            };
            // Try multiple ways to detect German language
            const isGerman = t('home.title') === 'Startseite' || 
                           t('status') === 'Status' || 
                           currentCulture?.includes('de') ||
                           navigator.language?.includes('de');
            const currentLang = isGerman ? 'de' : 'en';
            return dayNames[day.toLowerCase()]?.[currentLang] || day;
        }
        return translatedDay;
    };
    
    // Toggle time section visibility
    const toggleTimeSection = (day) => {
        setCollapsedTimeSections(prev => ({
            ...prev,
            [day]: !prev[day]
        }));
    };

    // Helper to get time value (local state takes precedence over props)
    const getTimeValue = (userId, day, index, type, originalValue) => {
        const key = `${userId}_${day}_${index}_${type}`;
        return localTimeValues[key] !== undefined ? localTimeValues[key] : toLocalTime(originalValue);
    };

    // Helper to set local time value
    const setLocalTimeValue = (userId, day, index, type, timeString) => {
        const key = `${userId}_${day}_${index}_${type}`;
        const localTime = timeString ? dayjs(timeString, 'HH:mm') : null;
        setLocalTimeValues(prev => ({
            ...prev,
            [key]: localTime
        }));
    };

    // Helper to sort work duration entries by start time
    const getSortedWorkDurations = (workDurationEmployees) => {
        if (!workDurationEmployees || workDurationEmployees.length === 0) return [];
        
        return [...workDurationEmployees].sort((a, b) => {
            // Convert start times to comparable values
            const startTimeA = a.startTime ? dayjs(a.startTime) : dayjs('00:00', 'HH:mm');
            const startTimeB = b.startTime ? dayjs(b.startTime) : dayjs('00:00', 'HH:mm');
            
            // If both times are invalid, maintain original order
            if (!startTimeA.isValid() && !startTimeB.isValid()) return 0;
            
            // Invalid times go to the end
            if (!startTimeA.isValid()) return 1;
            if (!startTimeB.isValid()) return -1;
            
            // Compare times
            return startTimeA.valueOf() - startTimeB.valueOf();
        });
    };

    // Enhanced visual feedback for user interactions
    const handleCardHover = (isHovering) => {
        if (!isMobile) {
            setExpandedCard(isHovering);
        }
    };
    
    const handleDayHover = (day, isHovering) => {
        if (!isMobile) {
            setHoveredDay(isHovering ? day : null);
        }
    };

    if(!calendar) return null;

    const openCommentModal = (day, userId) => {
        const calendar = currentWeekPlan.calendarEmployees?.[0];
        if (!calendar || !calendar.dailyPlanEmployees) return;
       
        const dailyPlan = calendar.dailyPlanEmployees.find((dp) => dp.day === day);
       
        if (!dailyPlan) return;
        setCurrentDay(day);
        setCurrentUserId(userId);
        setComment(dailyPlan?.comment || "");
        setIsModalVisible(true);
    };
    
    const handleModalOk = async () => {
        try {
            setIsLoading(true);
            logger.log("Saving comment for", currentDay, comment);
            await handleCommentChange(currentUserId, currentDay, comment);
            setIsModalVisible(false);
            // Show success feedback
            if (window.antd?.message) {
                window.antd.message.success(t('commentSavedSuccessfully'));
            }
        } catch (error) {
            logger.error("Error saving comment:", error);
            if (window.antd?.message) {
                window.antd.message.error(t('errorSavingComment'));
            }
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleModalCancel = () => {
        setIsModalVisible(false);
        // Reset comment to original value
        const calendar = currentWeekPlan.calendarEmployees?.[0];
        if (calendar && calendar.dailyPlanEmployees) {
            const dailyPlan = calendar.dailyPlanEmployees.find((dp) => dp.day === currentDay);
            setComment(dailyPlan?.comment || "");
        }
    };
    
    // Enhanced time change handler with visual feedback
    const handleTimeChangeWithFeedback = async (userId, day, durationIndex, type, timeString) => {
        try {
            setIsLoading(true);
            await handleTimeChange(userId, day, durationIndex, type, timeString);
        } catch (error) {
            logger.error("Error updating time:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleTimeChangeWithUtc = (userId, day, index, type, timeString) => {
        try {
            if (!timeString) {
                logger.warn("Empty timeString received in handleTimeChangeWithUtc");
                return;
            }
            
            // Update local state immediately for UI responsiveness
            setLocalTimeValue(userId, day, index, type, timeString);
            
            logger.log(`Time change: ${type} for ${day} at ${timeString}`);
            
            // The handleTimeChange from parent expects the timeString in ISO format
            // But looking at the parent implementation, it actually expects HH:mm format
            // Let's pass the timeString directly as it expects HH:mm format
            handleTimeChange(userId, day, index, type, timeString);
            
        } catch (error) {
            logger.error("Error in handleTimeChangeWithUtc:", error);
            // Revert local state on error
            setLocalTimeValue(userId, day, index, type, null);
        }
      };

    // Local handler for adding work duration
    const handleAddWorkDurationLocal = async (userId, day) => {
        try {
            if (!handleAddWorkDuration) {
                logger.warn("handleAddWorkDuration not provided as prop");
                return;
            }
            
            logger.log(`Adding work duration for user ${userId} on ${day}`);
            await handleAddWorkDuration(userId, day);
        } catch (error) {
            logger.error("Error adding work duration:", error);
        }
    };

    // Local handler for removing work duration
    const handleRemoveWorkDurationLocal = async (userId, day, durationIndex) => {
        try {
            if (!handleRemoveWorkDuration) {
                logger.warn("handleRemoveWorkDuration not provided as prop");
                return;
            }
            
            logger.log(`Removing work duration ${durationIndex} for user ${userId} on ${day}`);
            await handleRemoveWorkDuration(userId, day, durationIndex);
        } catch (error) {
            logger.error("Error removing work duration:", error);
        }
    };
    

  
      
    // --Large Screen Card Layout
    const renderLargeCard = (wp) => {
        const calendar = wp.calendarEmployees?.[0];
        if (!calendar || !calendar.dailyPlanEmployees) return null;
        const {hours, minutes} = calculateTotalWorkDuration(calendar.dailyPlanEmployees);
        const shouldHours = calendar.workTimeHours;
        const shouldMinutes = calendar.workTimeMinutes;
        const { diffHours, diffMinutesRemaining } = calculateWorkDurationDifference(shouldHours * 60 + shouldMinutes, hours * 60 + minutes);
        const isNegative = (hours*60+minutes) - (shouldHours*60+shouldMinutes) < 0;

    
        return (
            <Card key={wp.id} className={styles.largeCard}>
                <div className={styles.cardHeader}>
                    <h3>{formatUserName(wp, users, t)}</h3>
                    <h4>{t("totalWorktime") }:{hours}h {minutes}min</h4>
                    <h4>{t("shouldWorktime")}:{shouldHours}h {shouldMinutes}min</h4>
                   
                    <span style={{ color: isNegative ? 'red' : 'green' }}>
                    {formatDurationDifference(shouldHours*60+shouldMinutes, hours*60+minutes)}
                    </span>
                    

                   
                    <div>
                {
                    hasMediAdminRole && !hideActivate && (
                                <Tooltip title={t("activateAllWeekPlans") }>
                            <div className={styles.toggleSwitch}>
                                <label>{t("activate")}</label>
                                <Switch checked={calendar.activate}
                                onChange={(checked)=> handleActivateChanges(wp.userId, selectedWeek,selectedYear,checked)} 
                                />
                            </div>



                        </Tooltip>


                    )


                }    

                </div>
                    {!hideRestore && (
                    <Tooltip title={t("restoreWeekPlanFromTemplate") }>
                    <Button
                        type="primary"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => deleteUserWeekPlan(wp.userId, selectedWeek, selectedYear)}
                        size="small"
                    >{t("restore")}</Button>
                    </Tooltip>
                    )}
                    
                </div>
                
               
                <div className={styles.weekGrid}>
                    {days.map((day) => {
                        const dailyPlan = calendar.dailyPlanEmployees.find(dp => dp.day === day);
                        if(!dailyPlan) return null;
                        const displayStatus = dailyPlan ? mapIndexToStatus(dailyPlan.dayStatus) : "Work";
                       
                        return (
                            <div
                                key={day}
                                className={styles.dayCell}
                                style={{
                                    backgroundColor: getColorFromStatus(displayStatus),
                                    color: getTextColorForStatus(displayStatus)
                                }}
                            >
                                <div className={styles.dayHeader}>
                                    {getDayName(day)}
                                </div>
                                <div className={styles.dayDate}>
                                    {dayjs(getDateFromWeekAndDay(selectedYear, selectedWeek, day)).format('DD.MM.YYYY')}
                                </div>
    
                                <div className={styles.dayStatus}>
                                    <Tooltip title={t("status")}>
                                        <CheckCircleOutlined className={styles.iconLabel} />
                                    </Tooltip>
                                    <Select
                                            value={displayStatus}
                                            onChange={(value) => updateDayStatus(wp.userId, day, mapStatusToIndex(value))}
                                            size="small"
                                     >
                                        {dayStatuses.map((status) => (
                                            <Option key={status} value={status}>
                                                {t(`statuses.${status?.toLowerCase()}`)}
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                                {/* SubOrganisation Selection */}
                                {weekPlanSettings?.showSubOrganisation !== false && (
                                    <div className={styles.subOrganisationSelect}>
                                        <Tooltip title={t("subOrganisation")}>
                                            <TeamOutlined className={styles.iconLabel} />
                                        </Tooltip>
                                        <Select
                                            value={dailyPlan?.subOrganisationId || null}
                                            onChange={(value) => handleSubOrganisationChange(wp.userId, day, value)}
                                            placeholder={t("selectSubOrganisation")}
                                            allowClear
                                            size="small"
                                        >
                                            {subOrganisations?.map((subOrg) => (
                                                <Option key={subOrg.id} value={subOrg.id}>
                                                    {subOrg.name}
                                                    {subOrg.isDefault && (
                                                        <span style={{ color: '#1890ff', marginLeft: '4px' }}>
                                                            ({t("default")})
                                                        </span>
                                                    )}
                                                </Option>
                                            ))}
                                        </Select>
                                    </div>
                                )}
                                {/* Add Comment Button */}
                                {weekPlanSettings?.showComments !== false && (
                                    <Tooltip title={dailyPlan && dailyPlan.comment && dailyPlan.comment.trim().length > 0
                                      ? t("editComment")
                                      : t("addComment")
                                    }>
                                    <Button onClick={() => openCommentModal(day,wp.userId)} 
                                    size="small" className={`${styles.commentButton} ${
                                        dailyPlan && dailyPlan.comment && dailyPlan.comment.trim().length > 0
                                          ? styles.commentButtonHasComment
                                          : ""
                                      }`}
                                    icon={dailyPlan && dailyPlan.comment && dailyPlan.comment.trim().length > 0
                                        ?  <FormOutlined />
                                        : <MessageOutlined />
                                    }
                                    />
                                    </Tooltip>
                                )}
    
                                {weekPlanSettings?.showWorkDuration !== false && (
                                    <div className={styles.timeSection}>
                                        <div 
                                            className={styles.timeSectionHeader}
                                            onClick={() => toggleTimeSection(day)}
                                        >
                                            <Tooltip title={t("workingHours")}>
                                                <span className={styles.timeSectionTitle}>
                                                    <ClockCircleOutlined className={styles.iconLabel} />
                                                    <span className={styles.timeSectionText}>{t("workingHours")}</span>
                                                </span>
                                            </Tooltip>
                                            {collapsedTimeSections[day] ? <DownOutlined /> : <UpOutlined />}
                                        </div>
                                        <div className={`${styles.timeSectionContent} ${collapsedTimeSections[day] ? styles.timeSectionCollapsed : styles.timeSectionExpanded}`}>
                                            {getSortedWorkDurations(dailyPlan.workDurationEmployees).map((workDuration, sortedIndex) => {
                                                // Find the original index in the unsorted array
                                                const originalIndex = dailyPlan.workDurationEmployees.findIndex(
                                                    original => original === workDuration
                                                );
                                                return (
                                                <div key={`${originalIndex}-${workDuration.startTime || sortedIndex}`} className={styles.duratationGruop}>
                                                    <div className={styles.durationIndex}>
                                                        {sortedIndex + 1}
                                                    </div>
                                                    <div className={styles.durationControls}>
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            icon={<PlusOutlined />}
                                                            onClick={() => handleAddWorkDurationLocal(wp.userId, day)}
                                                            title={t("addWorkDuration")}
                                                        />
                                                        {dailyPlan.workDurationEmployees.length > 1 && (
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                danger
                                                                icon={<MinusOutlined />}
                                                                onClick={() => handleRemoveWorkDurationLocal(wp.userId, day, originalIndex)}
                                                                title={t("removeWorkDuration")}
                                                            />
                                                        )}
                                                    </div>
                                                    <div className={styles.timeRow}>
                                                        <Tooltip title={t("start")}>
                                                            <PlayCircleOutlined className={styles.iconLabel} />
                                                        </Tooltip>
                                                        <TimePicker
                                                        format="HH:mm"
                                                        size="small"
                                                        value={getTimeValue(wp.userId, day, originalIndex, 'startTime', workDuration.startTime)}
                                                        onChange={(time, timeString) => {
                                                            if (timeString) {
                                                                handleTimeChangeWithUtc(wp.userId, day, originalIndex, 'startTime', timeString);
                                                            }
                                                        }}
                                                        />
                                                    </div>
                                                    <div className={styles.timeRow}>
                                                        <Tooltip title={t("end")}>
                                                            <StopOutlined className={styles.iconLabel} />
                                                        </Tooltip>
                                                        <TimePicker
                                                            format="HH:mm"
                                                            size="small"
                                                            value={getTimeValue(wp.userId, day, originalIndex, 'endTime', workDuration.endTime)}
                                                            onChange={(time, timeString) => {
                                                                if (timeString) {
                                                                    handleTimeChangeWithUtc(wp.userId, day, originalIndex, 'endTime', timeString);
                                                                }
                                                            }}
                                                            />
                                                    </div>
                                                </div>
                                                );
                                            })}
                                            {/* Add button for first duration when none exist */}
                                            {(!dailyPlan.workDurationEmployees || dailyPlan.workDurationEmployees.length === 0) && (
                                                <Button
                                                    type="dashed"
                                                    size="small"
                                                    icon={<PlusOutlined />}
                                                    onClick={() => handleAddWorkDurationLocal(wp.userId, day)}
                                                    className={styles.addDurationButton}
                                                >
                                                    {t("addWorkTime")}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                )}

                            </div>
                        );
                    })}
                </div>
            </Card>
        );
    };

    // --Mobile Screen Card Layout
    const renderMobileCard=(wp)=>{
        const calendar = wp.calendarEmployees?.[0];
        if (!calendar || !calendar.dailyPlanEmployees) return null;
        return(
            <Card 
                key={wp.id} 
                title={
                    <div className={styles.employeeCardHeader}>
                        <h3>{formatUserName(wp, users, t)}</h3>
                        <div>
                {
                    hasMediAdminRole && (
                        <Tooltip title={t("activateAllWeekPlans")}>
                            <div className={styles.toggleSwitch}>
                                <label>{t("activate")}</label>
                                <Switch checked={calendar.activate}
                                onChange={(checked)=> handleActivateChanges(wp.userId, selectedWeek,selectedYear,checked)} 
                                />
                            </div>



                        </Tooltip>


                    )


                }    

                </div>
                        {!hideRestore && (
                        <Button
                            type="primary"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => deleteUserWeekPlan(wp.userId, selectedWeek, selectedYear)}
                            size="small"
                        >
                            {t("delete") }
                        </Button>
                        )}
                        </div>


                }
            
            className={styles.employeeCard}>
                {days.map((day)=>{


                    const dailyPlan=calendar.dailyPlanEmployees.find(dp=>dp.day===day);
                    if(!dailyPlan) return null;
                    const displayStatus=dailyPlan?mapIndexToStatus(dailyPlan.dayStatus):"Work";
                    return(
                        <div key={day}
                        className={styles.mobileDaySection}
                        style={{
                            backgroundColor: getColorFromStatus(displayStatus),
                            color: getTextColorForStatus(displayStatus)
                        }}>
                            <h4>{getDayName(day)}</h4>
                            <div className={styles.dayStatus}>
                                <Tooltip title={t("status")}>
                                    <CheckCircleOutlined className={styles.iconLabel} />
                                </Tooltip>
                                <Select
                                            value={displayStatus}
                                            onChange={(value) => updateDayStatus(wp.userId, day, mapStatusToIndex(value))}
                                            size="small"
                                     >
                                    {dayStatuses.map((status)=>(
                                        <Option key={status} value={status}>{t(`statuses.${status?.toLowerCase()}`)}</Option>
                                    ))}
                                </Select>
                            </div>
                            {/* SubOrganisation Selection */}
                            {weekPlanSettings?.showSubOrganisation !== false && (
                                <div className={styles.subOrganisationSelect}>
                                    <Tooltip title={t("subOrganisation")}>
                                        <TeamOutlined className={styles.iconLabel} />
                                    </Tooltip>
                                    <Select
                                        value={dailyPlan?.subOrganisationId || null}
                                        onChange={(value) => handleSubOrganisationChange(wp.userId, day, value)}
                                        placeholder={t("selectSubOrganisation")}
                                        allowClear
                                        size="small"
                                    >
                                        {subOrganisations?.map((subOrg) => (
                                            <Option key={subOrg.id} value={subOrg.id}>
                                                {subOrg.name}
                                                {subOrg.isDefault && (
                                                    <span style={{ color: '#1890ff', marginLeft: '4px' }}>
                                                        ({t("default")})
                                                    </span>
                                                )}
                                            </Option>
                                        ))}
                                    </Select>
                                </div>
                            )}
                            {/* Add Comment Button */}
                            {weekPlanSettings?.showComments !== false && (
                                <Button onClick={() => openCommentModal(day,wp.userId)} 
                                    size="small" className={`${styles.commentButton} ${
                                        dailyPlan && dailyPlan.comment && dailyPlan.comment.trim().length > 0
                                          ? styles.commentButtonHasComment
                                          : ""
                                      }`}
                                    >
                                    {dailyPlan && dailyPlan.comment && dailyPlan.comment.trim().length > 0
                                        ? t("editComment")
                                        : t("addComment")}
                                    </Button>
                            )}
                            {weekPlanSettings?.showWorkDuration !== false && (
                                <div className={styles.timeSection}>
                                    <div 
                                        className={styles.timeSectionHeader}
                                        onClick={() => toggleTimeSection(day)}
                                    >
                                        <Tooltip title={t("workingHours")}>
                                            <span className={styles.timeSectionTitle}>
                                                <ClockCircleOutlined className={styles.iconLabel} />
                                                <span className={styles.timeSectionText}>{t("workingHours")}</span>
                                            </span>
                                        </Tooltip>
                                        {collapsedTimeSections[day] ? <DownOutlined /> : <UpOutlined />}
                                    </div>
                                    <div className={`${styles.timeSectionContent} ${collapsedTimeSections[day] ? styles.timeSectionCollapsed : styles.timeSectionExpanded}`}>
                                        {getSortedWorkDurations(dailyPlan.workDurationEmployees).map((workDuration, sortedIndex) => {
                                            // Find the original index in the unsorted array
                                            const originalIndex = dailyPlan.workDurationEmployees.findIndex(
                                                original => original === workDuration
                                            );
                                            return (
                                            <div key={`${originalIndex}-${workDuration.startTime || sortedIndex}`} className={styles.mobileDurationGroup}>
                                                <div className={styles.durationIndex}>
                                                    {sortedIndex + 1}
                                                </div>
                                                <div className={styles.mobileDurationControls}>
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={<PlusOutlined />}
                                                        onClick={() => handleAddWorkDurationLocal(wp.userId, day)}
                                                        title={t("addWorkDuration")}
                                                    />
                                                    {dailyPlan.workDurationEmployees.length > 1 && (
                                                        <Button
                                                            type="text"
                                                            size="small"
                                                            danger
                                                            icon={<MinusOutlined />}
                                                            onClick={() => handleRemoveWorkDurationLocal(wp.userId, day, originalIndex)}
                                                            title={t("removeWorkDuration")}
                                                        />
                                                    )}
                                                </div>
                                                <div className={styles.mobileTimeRow}>
                                                    <label>{t("start")}: </label>
                                                    <TimePicker
                                                        format="HH:mm"
                                                        size="small"
                                                        value={getTimeValue(wp.userId, day, originalIndex, 'startTime', workDuration.startTime)}
                                                        onChange={(time, timeString) => {
                                                            if (timeString) {
                                                                handleTimeChangeWithUtc(wp.userId, day, originalIndex, 'startTime', timeString);
                                                            }
                                                        }}
                                                        />
                                                </div>
                                                <div className={styles.mobileTimeRow}>
                                                    <label>{t("end")}: </label>
                                                    <TimePicker
                                                            format="HH:mm"
                                                            size="small"
                                                            value={getTimeValue(wp.userId, day, originalIndex, 'endTime', workDuration.endTime)}
                                                            onChange={(time, timeString) => {
                                                                if (timeString) {
                                                                    handleTimeChangeWithUtc(wp.userId, day, originalIndex, 'endTime', timeString);
                                                                }
                                                            }}
                                                            />
                                                </div>
                                            </div>
                                            );
                                        })}
                                        {/* Add button for first duration when none exist */}
                                        {(!dailyPlan.workDurationEmployees || dailyPlan.workDurationEmployees.length === 0) && (
                                            <Button
                                                type="dashed"
                                                icon={<PlusOutlined />}
                                                onClick={() => handleAddWorkDurationLocal(wp.userId, day)}
                                                className={styles.addDurationButton}
                                                style={{marginTop: "12px"}}
                                            >
                                                {t("addWorkTime")}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
          
                    </Card>
        );
    };
    


return(
    <>
    {isMobile?
       ( <div className={styles.mobileContainer}>
            {
                renderMobileCard(currentWeekPlan)
            }
        </div>):
        (<div className={styles.scrollContainer}>
            <div className={styles.largeContainer}>

            {renderLargeCard(currentWeekPlan)}
                </div>
        </div>
    )}
    {/* NEW: Modal for editing the comment */}
    <Modal
        title={t("addCommentForDay")}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
      >
        <Input.TextArea
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
      </Modal>


        </>
   
);

};
